import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { statsAPI, simulationsAPI } from '../api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import { 
  ChartLine,
  Trophy,
  Clock,
  CalendarBlank,
  TrendUp,
  TrendDown,
  Eye,
  FilePdf,
  Spinner,
  ArrowRight,
  CheckCircle,
  XCircle,
  Heartbeat
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { reportAPI } from '../api';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

export default function HistoryPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(null);

  useEffect(() => {
    loadHistory();
  }, [user]);

  const loadHistory = async () => {
    try {
      const [historyRes, statsRes] = await Promise.all([
        statsAPI.getUserHistory(user.id),
        statsAPI.getDashboard()
      ]);
      setHistory(historyRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Error al cargar historial');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async (sessionId) => {
    setDownloadingPdf(sessionId);
    try {
      const response = await reportAPI.downloadPDF(sessionId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `SimuClinic_Informe_${sessionId.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF descargado');
    } catch (error) {
      toast.error('Error al descargar PDF');
    } finally {
      setDownloadingPdf(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CL', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTime = (seconds) => {
    if (!seconds) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate stats from history
  const completedSimulations = history.filter(h => h.status === 'finalizada');
  const averageScore = completedSimulations.length > 0
    ? Math.round(completedSimulations.reduce((acc, h) => acc + (h.debriefing?.analysis?.puntuacion_total || 0), 0) / completedSimulations.length)
    : 0;
  const totalTime = history.reduce((acc, h) => acc + (h.debriefing?.analysis?.tiempo_total || 0), 0);

  // Prepare chart data
  const scoreOverTime = completedSimulations
    .slice(-10)
    .map((h, i) => ({
      name: `Sim ${i + 1}`,
      score: h.debriefing?.analysis?.puntuacion_total || 0,
      date: new Date(h.created_at).toLocaleDateString('es-CL', { day: '2-digit', month: 'short' })
    }));

  const specialtyData = history.reduce((acc, h) => {
    const specialty = h.scenario_name?.split(' ')[0] || 'Otro';
    acc[specialty] = (acc[specialty] || 0) + 1;
    return acc;
  }, {});

  const pieData = Object.entries(specialtyData).map(([name, value]) => ({ name, value }));

  if (loading) {
    return (
      <>
        <div className="flex items-center justify-center h-64">
          <Spinner size={48} className="animate-spin text-blue-600" />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold font-['Chivo'] text-slate-900" data-testid="history-title">
            Mi Historial de Simulaciones
          </h1>
          <p className="text-slate-500">
            Revisa tu progreso y rendimiento
          </p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">Total Simulaciones</p>
                  <p className="text-3xl font-bold">{history.length}</p>
                </div>
                <Heartbeat size={40} className="text-blue-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm">Completadas</p>
                  <p className="text-3xl font-bold">{completedSimulations.length}</p>
                </div>
                <CheckCircle size={40} className="text-green-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">Puntaje Promedio</p>
                  <p className="text-3xl font-bold">{averageScore}</p>
                </div>
                <Trophy size={40} className="text-purple-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-100 text-sm">Tiempo Total</p>
                  <p className="text-3xl font-bold">{Math.round(totalTime / 60)}m</p>
                </div>
                <Clock size={40} className="text-orange-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Score Over Time */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ChartLine size={20} />
                Evolución de Puntaje
              </CardTitle>
            </CardHeader>
            <CardContent>
              {scoreOverTime.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={scoreOverTime}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#9CA3AF" />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} stroke="#9CA3AF" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1F2937', 
                        border: 'none', 
                        borderRadius: '8px',
                        color: 'white'
                      }} 
                    />
                    <Line 
                      type="monotone" 
                      dataKey="score" 
                      stroke="#3B82F6" 
                      strokeWidth={3}
                      dot={{ fill: '#3B82F6', strokeWidth: 2 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-slate-400">
                  <p>Completa simulaciones para ver tu progreso</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Specialty Distribution */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendUp size={20} />
                Por Especialidad
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-slate-400">
                  <p>Sin datos</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* History List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarBlank size={20} />
              Historial Detallado
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[400px]">
              <div className="divide-y">
                {history.length > 0 ? history.map((session) => (
                  <div key={session.id} className="p-4 hover:bg-slate-50 transition-colors" data-testid={`history-item-${session.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-medium text-slate-900">{session.scenario_name || 'Escenario'}</h3>
                          {session.status === 'finalizada' ? (
                            <Badge className="bg-green-100 text-green-700">
                              <CheckCircle size={12} className="mr-1" weight="fill" />
                              Completada
                            </Badge>
                          ) : (
                            <Badge className="bg-yellow-100 text-yellow-700">
                              En progreso
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <CalendarBlank size={14} />
                            {formatDate(session.created_at)}
                          </span>
                          {session.debriefing?.analysis?.tiempo_total && (
                            <span className="flex items-center gap-1">
                              <Clock size={14} />
                              {formatTime(session.debriefing.analysis.tiempo_total)}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        {session.debriefing?.analysis && (
                          <div className="text-right">
                            <p className="text-2xl font-bold text-slate-900">
                              {session.debriefing.analysis.puntuacion_total}
                            </p>
                            <p className="text-xs text-slate-500">puntos</p>
                          </div>
                        )}
                        
                        <div className="flex gap-2">
                          {session.debriefing && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownloadPDF(session.id)}
                              disabled={downloadingPdf === session.id}
                              data-testid={`download-pdf-${session.id}`}
                            >
                              {downloadingPdf === session.id ? (
                                <Spinner size={16} className="animate-spin" />
                              ) : (
                                <FilePdf size={16} />
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {session.debriefing?.analysis && (
                      <div className="mt-3 pt-3 border-t">
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <p className="text-xs text-slate-500 mb-1">Técnica</p>
                            <Progress value={session.debriefing.analysis.puntuacion_tecnica} className="h-2" />
                            <p className="text-xs text-right mt-1">{session.debriefing.analysis.puntuacion_tecnica}/100</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500 mb-1">No Técnica</p>
                            <Progress value={session.debriefing.analysis.puntuacion_no_tecnica} className="h-2" />
                            <p className="text-xs text-right mt-1">{session.debriefing.analysis.puntuacion_no_tecnica}/100</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500 mb-1">Anamnesis</p>
                            <Progress value={session.debriefing.analysis.calidad_anamnesis || 0} className="h-2" />
                            <p className="text-xs text-right mt-1">{session.debriefing.analysis.calidad_anamnesis || 0}/100</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )) : (
                  <div className="p-8 text-center text-slate-400">
                    <Heartbeat size={48} className="mx-auto mb-4" />
                    <p>No tienes simulaciones aún</p>
                    <Button 
                      className="mt-4" 
                      onClick={() => navigate('/app/scenarios')}
                    >
                      Comenzar Primera Simulación
                    </Button>
                  </div>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
